import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import anime from 'animejs';
import { FaBrain, FaCogs, FaChartLine, FaComments, FaRobot, FaGraduationCap } from 'react-icons/fa';
import './Services.css';

const Services = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const gridRef = useRef(null);

  useEffect(() => {
    if (inView && gridRef.current) {
      // Anime.js staggered animation for service cards
      anime({
        targets: '.service-card',
        translateY: [100, 0],
        opacity: [0, 1],
        rotateX: [-15, 0],
        scale: [0.8, 1],
        easing: 'easeOutElastic(1, .8)',
        duration: 1500,
        delay: anime.stagger(100)
      });

      // Animate icons
      anime({
        targets: '.service-icon',
        scale: [0, 1],
        rotate: [0, 360],
        easing: 'easeOutElastic(1, .6)',
        duration: 2000,
        delay: anime.stagger(100, { start: 300 })
      });

      // Continuous floating animation for icons
      anime({
        targets: '.service-icon',
        translateY: [-10, 10],
        easing: 'easeInOutSine',
        duration: 2000,
        loop: true,
        direction: 'alternate',
        delay: anime.stagger(200)
      });
    }
  }, [inView]);

  const services = [
    {
      icon: <FaBrain />,
      title: 'AI Integration & Implementation',
      description: 'Seamlessly integrate cutting-edge AI models into your existing workflows. We connect GPT, Claude, and custom ML models to your systems, delivering 3x faster processing and 95% accuracy rates.'
    },
    {
      icon: <FaCogs />,
      title: 'Intelligent Process Automation',
      description: 'Automate repetitive tasks with smart RPA solutions. From invoice processing to data entry, reduce manual work by 90% and eliminate human errors while scaling operations effortlessly.'
    },
    {
      icon: <FaChartLine />,
      title: 'Predictive Analytics & BI',
      description: 'Transform raw data into actionable insights using advanced ML algorithms. Forecast trends, predict customer behavior, and make informed decisions with real-time analytics dashboards.'
    },
    {
      icon: <FaComments />,
      title: 'AI Chatbots & Virtual Assistants',
      description: 'Deploy intelligent conversational AI that understands context and intent. Provide 24/7 customer support, handle 1000+ conversations simultaneously, and boost satisfaction by 60%.'
    },
    {
      icon: <FaRobot />,
      title: 'Custom AI Solutions',
      description: 'Bespoke AI systems tailored to your unique challenges. From computer vision for quality control to NLP for document processing - we build exactly what you need to succeed.'
    },
    {
      icon: <FaGraduationCap />,
      title: 'AI Training & Consulting',
      description: 'Empower your team with comprehensive AI training programs. Expert consulting, hands-on workshops, and ongoing support to ensure successful AI adoption across your organization.'
    }
  ];

  const cardVariants = {
    hidden: { y: 50, opacity: 0, rotateX: -15 },
    visible: {
      y: 0,
      opacity: 1,
      rotateX: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };

  return (
    <section id="services" className="section services">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: -50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          ref={ref}
        >
          <h2>Our Services</h2>
          <p>Comprehensive AI automation solutions tailored to your business needs</p>
        </motion.div>

        <div
          className="services-grid"
          ref={gridRef}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="service-card glass"
              variants={cardVariants}
              whileHover={{
                scale: 1.05,
                rotateY: 5,
                rotateX: 5,
                transition: { duration: 0.3 }
              }}
              style={{ perspective: 1000 }}
            >
              <motion.div
                className="service-icon"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              >
                {service.icon}
              </motion.div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <div className="card-glow"></div>
              
              {/* Animated particles in card */}
              <div className="card-particles">
                <span className="particle"></span>
                <span className="particle"></span>
                <span className="particle"></span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
